import React, { useEffect, useState } from 'react';
import { landlordAPI } from '../../lib/api';
import { formatCurrency, formatDate, downloadBlob } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  BarChart3, FileSpreadsheet, FileText, Loader2, DoorOpen,
  TrendingUp, TrendingDown, Wallet, Users, Clock, AlertCircle,
  CheckCircle, Home, Calendar, Search, Phone, Mail, Building2,
  Receipt, UserCheck, UserX, DollarSign
} from 'lucide-react';
import { ReportsOverviewTab } from '../../components/landlord/reports/ReportsOverviewTab';
import { ReportsAnalyticsTab } from '../../components/landlord/reports/ReportsAnalyticsTab';
import { ReportsPaymentsTab } from '../../components/landlord/reports/ReportsPaymentsTab';

export default function LandlordReports() {
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const response = await landlordAPI.getComprehensiveReport();
      setReports(response.data);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await landlordAPI.exportComprehensiveReport(format);
      const filename = format === 'excel' ? 'dormhub_comprehensive_report.xlsx' : 'dormhub_comprehensive_report.pdf';
      downloadBlob(response.data, filename);
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const { financial_summary, monthly_breakdown, tenants, payments, properties } = reports || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const filterData = (data, fields) => {
    if (!searchTerm) return data;
    return data?.filter(item =>
      fields.some(field =>
        item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    ) || [];
  };

  return (
    <div className="space-y-6" data-testid="landlord-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">Reports & Analytics</h1>
          <p className="text-muted-foreground mt-1">Comprehensive property management insights</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport('excel')} disabled={exporting} data-testid="export-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
          <Button variant="outline" onClick={() => handleExport('pdf')} disabled={exporting} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        </div>
      </div>

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs font-medium">Total Income</span>
            </div>
            <p className="text-xl font-bold text-emerald-700">{formatCurrency(financial_summary?.total_income || 0)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600 mb-1">
              <TrendingDown className="w-4 h-4" />
              <span className="text-xs font-medium">Total Expenses</span>
            </div>
            <p className="text-xl font-bold text-red-700">{formatCurrency(financial_summary?.total_expenses || 0)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs font-medium">Net Income</span>
            </div>
            <p className="text-xl font-bold text-blue-700">{formatCurrency(financial_summary?.net_income || 0)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#C85A42]/10 to-[#C85A42]/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#C85A42] mb-1">
              <UserCheck className="w-4 h-4" />
              <span className="text-xs font-medium">Active Tenants</span>
            </div>
            <p className="text-xl font-bold text-[#B04A35]">{tenants?.total_active || 0}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium">Pending Verify</span>
            </div>
            <p className="text-xl font-bold text-amber-700">{payments?.pending?.length || 0}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <DoorOpen className="w-4 h-4" />
              <span className="text-xs font-medium">Vacant Units</span>
            </div>
            <p className="text-xl font-bold text-purple-700">{properties?.occupancy?.reduce((sum, p) => sum + p.vacant, 0) || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Different Reports */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-4 lg:grid-cols-9 h-auto gap-1 p-1 bg-muted/50">
          <TabsTrigger value="overview" className="text-xs px-2 py-2" data-testid="tab-overview">
            <BarChart3 className="w-3 h-3 mr-1 hidden sm:inline" />Overview
          </TabsTrigger>
          <TabsTrigger value="analytics" className="text-xs px-2 py-2" data-testid="tab-analytics">
            <TrendingUp className="w-3 h-3 mr-1 hidden sm:inline" />Analytics
          </TabsTrigger>
          <TabsTrigger value="financial" className="text-xs px-2 py-2" data-testid="tab-financial">
            <Wallet className="w-3 h-3 mr-1 hidden sm:inline" />Financial
          </TabsTrigger>
          <TabsTrigger value="tenants" className="text-xs px-2 py-2" data-testid="tab-tenants">
            <Users className="w-3 h-3 mr-1 hidden sm:inline" />Tenants
          </TabsTrigger>
          <TabsTrigger value="payments" className="text-xs px-2 py-2" data-testid="tab-payments">
            <Receipt className="w-3 h-3 mr-1 hidden sm:inline" />Payments
          </TabsTrigger>
          <TabsTrigger value="pending" className="text-xs px-2 py-2" data-testid="tab-pending">
            <Clock className="w-3 h-3 mr-1 hidden sm:inline" />Pending
          </TabsTrigger>
          <TabsTrigger value="unpaid" className="text-xs px-2 py-2" data-testid="tab-unpaid">
            <AlertCircle className="w-3 h-3 mr-1 hidden sm:inline" />Unpaid
          </TabsTrigger>
          <TabsTrigger value="units" className="text-xs px-2 py-2" data-testid="tab-units">
            <Home className="w-3 h-3 mr-1 hidden sm:inline" />Units
          </TabsTrigger>
          <TabsTrigger value="history" className="text-xs px-2 py-2" data-testid="tab-history">
            <Calendar className="w-3 h-3 mr-1 hidden sm:inline" />History
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <ReportsOverviewTab
            monthlyBreakdown={monthly_breakdown}
            financialSummary={financial_summary}
            tenants={tenants}
            payments={payments}
            properties={properties}
          />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <ReportsAnalyticsTab />
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Financial Summary</CardTitle>
              <CardDescription>Detailed breakdown of income and expenses</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 bg-emerald-50 dark:bg-emerald-950 rounded-xl text-center">
                  <TrendingUp className="w-8 h-8 text-emerald-600 dark:text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Total Income</p>
                  <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(financial_summary?.total_income || 0)}</p>
                </div>
                <div className="p-6 bg-red-50 dark:bg-red-950 rounded-xl text-center">
                  <TrendingDown className="w-8 h-8 text-red-600 dark:text-red-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Total Expenses</p>
                  <p className="text-3xl font-bold text-red-700 dark:text-red-300">{formatCurrency(financial_summary?.total_expenses || 0)}</p>
                </div>
                <div className="p-6 bg-blue-50 dark:bg-blue-950 rounded-xl text-center">
                  <DollarSign className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Net Income</p>
                  <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">{formatCurrency(financial_summary?.net_income || 0)}</p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-950 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    <span className="font-medium text-amber-900 dark:text-amber-100">Pending Payments</span>
                  </div>
                  <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{formatCurrency(financial_summary?.pending_payments || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Active Tenants Tab */}
        <TabsContent value="tenants" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Active Tenants & Demographics</CardTitle>
                  <CardDescription>Current tenants with their assigned units</CardDescription>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tenants..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Contact</th>
                      <th>Property</th>
                      <th>Unit</th>
                      <th>Monthly Rent</th>
                      <th>Lease Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterData(tenants?.active, ['tenant_name', 'tenant_email', 'property_name', 'unit_number'])?.map((tenant, idx) => (
                      <tr key={tenant.tenant_id || tenant.tenant_email || `tenant-${idx}`}>
                        <td>
                          <div className="font-medium">{tenant.tenant_name}</div>
                        </td>
                        <td>
                          <div className="text-sm">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Mail className="w-3 h-3" />{tenant.tenant_email}
                            </div>
                            {tenant.tenant_phone && (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Phone className="w-3 h-3" />{tenant.tenant_phone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            {tenant.property_name}
                          </div>
                        </td>
                        <td><Badge variant="outline">{tenant.unit_number}</Badge></td>
                        <td className="font-semibold text-emerald-600">{formatCurrency(tenant.monthly_rent)}</td>
                        <td className="text-sm text-muted-foreground">
                          {tenant.lease_start} to {tenant.lease_end}
                        </td>
                      </tr>
                    ))}
                    {(!tenants?.active?.length) && (
                      <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No active tenants</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Demographics Summary */}
          <Card>
            <CardHeader>
              <CardTitle>All Active Tenants</CardTitle>
              <CardDescription>Summary of current tenants</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-[#C85A42] mx-auto mb-4" />
                <p className="text-3xl font-bold">{tenants?.total_active || 0}</p>
                <p className="text-muted-foreground">Active Tenants</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Received Tab */}
        <TabsContent value="payments" className="space-y-4">
          <ReportsPaymentsTab received={payments?.received} />
        </TabsContent>

        {/* Pending Verification Tab */}
        <TabsContent value="pending" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Payments Pending Verification
              </CardTitle>
              <CardDescription>Payments that need your review and approval</CardDescription>
            </CardHeader>
            <CardContent>
              {payments?.pending?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Tenant</th>
                        <th>Contact</th>
                        <th>Unit</th>
                        <th>Amount</th>
                        <th>Type</th>
                        <th>Method</th>
                        <th>Period</th>
                        <th>Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments?.pending?.map((payment, idx) => (
                        <tr key={payment.payment_id || `pending-${idx}`}>
                          <td className="font-medium">{payment.tenant_name || payment.tenant_id}</td>
                          <td className="text-sm text-muted-foreground">{payment.tenant_email || '-'}</td>
                          <td><Badge variant="outline">{payment.unit_number || '-'}</Badge></td>
                          <td className="font-semibold text-amber-600">{formatCurrency(payment.amount)}</td>
                          <td className="capitalize">{payment.payment_type}</td>
                          <td className="capitalize">{payment.payment_method}</td>
                          <td>{payment.period_month}</td>
                          <td className="text-sm">{payment.created_at ? formatDate(payment.created_at) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                  <p className="text-lg font-medium">All caught up!</p>
                  <p className="text-muted-foreground">No payments pending verification</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Tenants */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserX className="w-5 h-5 text-amber-500" />
                Pending Tenants
              </CardTitle>
              <CardDescription>Tenants who haven't acknowledged their lease yet</CardDescription>
            </CardHeader>
            <CardContent>
              {tenants?.pending?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Tenant</th>
                        <th>Email</th>
                        <th>Property</th>
                        <th>Unit</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants?.pending?.map((tenant, idx) => (
                        <tr key={tenant.tenant_id || tenant.tenant_email || `pending-tenant-${idx}`}>
                          <td className="font-medium">{tenant.tenant_name}</td>
                          <td className="text-muted-foreground">{tenant.tenant_email}</td>
                          <td>{tenant.property_name}</td>
                          <td><Badge variant="outline">{tenant.unit_number}</Badge></td>
                          <td className="text-sm">{tenant.created_at ? formatDate(tenant.created_at) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No pending tenants
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unpaid Rent Tab */}
        <TabsContent value="unpaid" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                Pending Payments Overview
              </CardTitle>
              <CardDescription>Payments awaiting verification</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <div className="text-3xl font-bold text-amber-600 mb-2">{formatCurrency(financial_summary?.pending_payments || 0)}</div>
                <p className="text-muted-foreground">Total pending payment amount</p>
                <p className="text-sm text-muted-foreground mt-4">
                  {payments?.pending?.length || 0} payment(s) awaiting verification
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Units Tab */}
        <TabsContent value="units" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DoorOpen className="w-5 h-5 text-blue-500" />
                Property Units Overview
              </CardTitle>
              <CardDescription>Unit status by property</CardDescription>
            </CardHeader>
            <CardContent>
              {properties?.units?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {properties?.units?.filter(u => u.status === 'vacant')?.map((unit, idx) => (
                    <Card key={unit.unit_id || `unit-${idx}`} className="border-dashed">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-bold text-lg">{unit.unit_number}</p>
                            <p className="text-sm text-muted-foreground">{unit.property_name}</p>
                          </div>
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">Vacant</Badge>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Floor</span>
                            <span>{unit.floor || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Capacity</span>
                            <span>{unit.capacity} person(s)</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Monthly Rent</span>
                            <span className="font-semibold text-[#C85A42]">{formatCurrency(unit.monthly_rent)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                  <p className="text-lg font-medium">All units occupied!</p>
                  <p className="text-muted-foreground">No vacant units available</p>
                </div>
              )}
              {properties?.units?.filter(u => u.status === 'vacant')?.length === 0 && properties?.units?.length > 0 && (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                  <p className="text-lg font-medium">All units occupied!</p>
                  <p className="text-muted-foreground">No vacant units available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tenant History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-500" />
                    Tenant History
                  </CardTitle>
                  <CardDescription>Complete history of all lease agreements</CardDescription>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search history..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Property</th>
                      <th>Unit</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Status</th>
                      <th>Monthly Rent</th>
                      <th>Total Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterData(tenants?.history || tenants?.active, ['tenant_name', 'property_name', 'unit_number', 'status'])?.map((lease, idx) => (
                      <tr key={lease.lease_id || `history-${idx}`}>
                        <td className="font-medium">{lease.tenant_name}</td>
                        <td>{lease.property_name}</td>
                        <td><Badge variant="outline">{lease.unit_number}</Badge></td>
                        <td>{lease.lease_start || lease.start_date}</td>
                        <td>{lease.lease_end || lease.end_date}</td>
                        <td>
                          <Badge
                            variant={lease.status === 'active' ? 'default' : 'secondary'}
                            className={
                              lease.status === 'active' ? 'bg-emerald-500' :
                              lease.status === 'expired' ? 'bg-gray-500' :
                              lease.status === 'terminated' ? 'bg-red-500' :
                              lease.status === 'pending' ? 'bg-amber-500' : ''
                            }
                          >
                            {lease.status || 'active'}
                          </Badge>
                        </td>
                        <td>{formatCurrency(lease.monthly_rent)}</td>
                        <td className="font-semibold text-emerald-600">{formatCurrency(lease.total_paid || 0)}</td>
                      </tr>
                    ))}
                    {(!(tenants?.history?.length || tenants?.active?.length)) && (
                      <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No lease history</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
